// Polish Phase 1 (AUDIO-03 / A11Y-AUDIO-01) — SFX preference store.
//
// The web client had NO audio of any kind and no mute control. This store is
// the single source of truth for whether SFX play and how loud, persisted in
// localStorage alongside the other `runegambit.*` prefs ($themeKey, $locale,
// $motionPref). The AudioEngine (src/game/audio/engine.ts) subscribes to
// $sfxGain and ramps its SFX bus accordingly; the Settings → Sound card
// (src/ui/settings/SoundToggle.tsx) writes $sfxMuted / $sfxVolume.
//
// WCAG 1.4.2 (Audio Control): a mechanism to turn sound OFF is the a11y
// requirement. `$sfxMuted` is that mechanism. Volume is a refinement.
//
// REDUCED-MOTION-vs-AUDIO POLICY (AUDIO-08), codified here as the contract:
//   Reduced motion governs *visual motion* (vestibular comfort) — it suppresses
//   particles, flattens springs, collapses cinematic camera moves. It does NOT
//   mute audio. SFX are short, non-parallax, and convey gameplay state; a
//   player on reduced motion still hears the token clink and the invalid-word
//   buzz. The explicit, discoverable control for "no sound" is $sfxMuted, NOT
//   the motion preference. The engine therefore gates playback on mute only,
//   never on $reducedMotion. (Big-moment LAYERED sequences are unaffected too —
//   the cinematic's *visuals* shorten under reduced motion, the cue still
//   plays.) This mirrors BrandIdentity §13 "silence is a brand value": the
//   default soundscape is sparse, and the user owns the off switch.

import { persistentAtom } from '@nanostores/persistent'
import { computed } from 'nanostores'

/** Three-step volume — exposed as a segmented control (no slider primitive
 * exists yet; three steps satisfy the §7.4 "speaker volume" affordance with
 * the shared <Seg> radiogroup and stay keyboard-navigable). */
export type SfxVolume = 'quiet' | 'normal' | 'loud'

const SFX_VOLUMES: ReadonlySet<SfxVolume> = new Set(['quiet', 'normal', 'loud'])

/** Linear bus gain per volume step. `normal` ≈ 0.8 so layered big-moment cues
 * have headroom under the master limiter without clipping. */
export const SFX_VOLUME_GAIN: Readonly<Record<SfxVolume, number>> = {
  quiet: 0.45,
  normal: 0.8,
  loud: 1,
}

function isSfxVolume(v: unknown): v is SfxVolume {
  return typeof v === 'string' && SFX_VOLUMES.has(v as SfxVolume)
}

/** Master SFX mute. Default OFF (sound on) — but the soundscape is sparse by
 * design, so "on" is not noisy. WCAG 1.4.2 mechanism. */
export const $sfxMuted = persistentAtom<boolean>(
  'runegambit.sfx-muted',
  false,
  {
    encode: (v) => (v ? '1' : '0'),
    decode: (s) => s === '1',
  },
)

/** SFX volume step. */
export const $sfxVolume = persistentAtom<SfxVolume>(
  'runegambit.sfx-volume',
  'normal',
  {
    encode: (v) => v,
    decode: (s) => (isSfxVolume(s) ? s : 'normal'),
  },
)

/**
 * Resolved linear SFX bus gain in [0, 1]. The engine binds this to its SFX
 * bus GainNode. Muted → 0 (hard silence); otherwise the volume-step gain.
 */
export const $sfxGain = computed([$sfxMuted, $sfxVolume], (muted, volume) =>
  muted ? 0 : SFX_VOLUME_GAIN[volume],
)
